import {
  Button,
  Icon,
  IconButton,
  ReverseScroll,
  ReverseScrollRef,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import EmojiOne from 'emojione';
import { accessControl } from 'lib-shared-access-control';
import {
  AsyncStatus,
  Message,
  MetaConversation,
} from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import type {
  ConversationSummaryInfoSummary,
  User,
} from 'lib-shared-types-api';
import findLast from 'lodash/findLast';
import _findLastIndex from 'lodash/findLastIndex';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { Fragment } from 'react';

import { PHONE_BASED_CHANNELS } from 'app/utils/channels';
import { ConversationSummaryByCsid } from 'components/conversation';
import { useCommandHandler } from 'utils/commands';

import { prepareOriginalEmailUrl } from '../../../../app/utils/messages';
import AssistantSummary from '../../containers/assistant-summary';
import OriginalEmailModal from '../../containers/originalEmailModal';
import PrinterFriendly from '../../containers/printerFriendly';
import type { EmailMessageType } from '../../reducers/emailMessage';
import type { TypingType } from '../../reducers/typing';
import { Typing } from '../Typing';
import { EmailHeadersModal } from '../emailHeadersModal/emailHeadersModal';
import MessageEmail from '../messages/MessageEmail';
import MessageEmailHTML from '../messages/MessageEmailHTML';
import { MessageFacebook } from '../messages/MessageFacebook';
import { MessageInstagram } from '../messages/MessageInstagram';
import MessagePhone from '../messages/MessagePhone';
import { MessageSms } from '../messages/MessageSms';
import { MessageTranscript } from '../messages/MessageTranscript';
import { MessageTwitter } from '../messages/MessageTwitter';
import MessageVoicemail from '../messages/MessageVoicemail';
import { MessageWhatsApp } from '../messages/MessageWhatsApp';
import MessageWidgetChat from '../messages/MessageWidgetChat';
import NewMessagesDevider from '../messages/NewMessagesDevider';
import StatusAbandoned from '../messages/StatusAbandoned';
import { StatusAutoReply } from '../messages/StatusAutoReply';
import StatusClosed from '../messages/StatusClosed';
import StatusIsForward from '../messages/StatusIsForward';
import StatusTransferAnswered from '../messages/StatusTransferAnswered';
import StatusTransferStarted from '../messages/StatusTransferStarted';
import StatusTransferStatus from '../messages/StatusTransferStatus';
import StatusUnassigned from '../messages/StatusUnassigned';
import TextMessage, { TextMessageProps } from '../messages/TextMessage';
import { RedactMessageModal } from '../redactMessageModal/redactMessageModal';

import '../../styles/messages.less';
import { derivePredictedQualityScoresForMessages } from './utils';

type Props = {
  asyncStatus: {
    getConversation: AsyncStatus;
    getOutboundAddress: AsyncStatus;
    submit: AsyncStatus;
    close: AsyncStatus;
    wrapup: AsyncStatus;
    claim: AsyncStatus;
    tags: AsyncStatus;
    pending: AsyncStatus;
  };
  metaConversation: MetaConversation;
  htmlMessages: Array<EmailMessageType>;
  typings: Array<TypingType>;
  messages: Array<any>;
  thisUserId: string | null;
  originalEmailMeta: {
    fetchOriginalEmail: AsyncStatus;
    printerFriendly: AsyncStatus;
    htmlMessage: AsyncStatus;
  };
  enableCallRecordings: boolean;

  conversationGet: (payload: { csid: string }) => Promise<any>;
  originalEmailGet: (payload: { url: string }) => Promise<any>;
  originalEmailGetPrinterFriendly: (payload: { url: string }) => Promise<any>;
  originalEmailGetHtmlMessage: (payload: {
    url: string;
    csid: number;
  }) => Promise<any>;
  originalEmailHtmlGetByType: (payload: {
    url: string;
    type: string;
  }) => Promise<any>;
  conversationSetSummary: (
    summary: null | ConversationSummaryInfoSummary,
  ) => void;
  useShadowDom?: boolean;
};

type StateT = {
  newMessagesContent: any;
  newMessagesClass: string;
  scrollAtBottom: boolean;
  isFirstPhoneMessage: boolean;
  htmlIframeSrc?: any;
};

type MessageProps = TextMessageProps &
  Partial<{
    key: string;
    originalEmailMeta: {
      fetchOriginalEmail: AsyncStatus;
      printerFriendly: AsyncStatus;
      htmlMessage: AsyncStatus;
    };
    htmlMessages: Array<EmailMessageType>;
    originalEmailGetHtmlMessage: (payload: {
      url: string;
      csid: number;
    }) => Promise<any>;
    onFetchPrinter: () => void;
    onShowOriginal?: () => void;
    recordingURL: string;
    originalUrl: string;
  }>;

EmojiOne.ascii = true;

const renderedMessageTypes = [
  'newMissedMessagesDevider',
  'conversation:add_message',
  'conversation:message_added',
  'conversation:wrapup_message',
  'conversation:wrapup_added',
  'conversation:closed',
  'conversation:agent_unassigned',
  'conversation:transfer_initiated',
  'conversation:transfer_status',
  'conversation:transfer_answered',
  'conversation:rating_scheduled',
  'conversation:rating_unscheduled',
  'conversation:rating_offered',
  'conversation:rating_cancelled',
  'conversation:rated',
  'conversation:auto_reply_sent',
] as const;

type RenderedMessageType = (typeof renderedMessageTypes)[number];

const includedInSummaryMessageTypes: RenderedMessageType[] = [
  'conversation:message_added',
  'conversation:add_message',
];

export default class MessageSection extends React.Component<Props, StateT> {
  messagesRef: React.RefObject<any>;
  reverseScrollRef: React.RefObject<ReverseScrollRef>;

  messagesSection: any;
  timeouts: number[];
  scrollToLastItemTimeout: number | null;

  constructor(props: Props) {
    super(props);
    this.timeouts = [];
    this.scrollToLastItemTimeout = null;
    this._closeAndScrollToNewMessage =
      this._closeAndScrollToNewMessage.bind(this);
    this.onScrollHandler = this.onScrollHandler.bind(this);
    this.getConversation = this.getConversation.bind(this);
    this.setSummary = this.setSummary.bind(this);
    this.state = {
      newMessagesContent: null,
      newMessagesClass: 'hidden',
      scrollAtBottom: true,
      isFirstPhoneMessage: false,
    };

    this.messagesRef = React.createRef();
    this.reverseScrollRef = React.createRef();
  }

  componentWillUnmount() {
    if (this.timeouts !== null) {
      for (const timeout of this.timeouts) {
        window.clearTimeout(timeout);
      }
    }

    if (this.scrollToLastItemTimeout !== null) {
      window.clearTimeout(this.scrollToLastItemTimeout);
    }
  }

  componentDidMount() {
    this.scrollToLastItem();
    this.moveScrollToLastItemIfNeeded();
  }

  shouldComponentUpdate(nProps: Props, nState: StateT) {
    return !isEqual(this.props, nProps) || !isEqual(this.state, nState);
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.metaConversation.conversation.id !==
      prevProps.metaConversation.conversation.id
    ) {
      this.setState({ scrollAtBottom: true });
      this.scrollToLastItem();
      this.moveScrollToLastItemIfNeeded();
    }

    if (
      this.state.scrollAtBottom === true &&
      prevProps.messages.length < this.props.messages.length
    ) {
      this.scrollToLastItem();
    } else if (
      this.state.scrollAtBottom === false &&
      prevProps.messages.filter(this.filterMessages).length <
        this.props.messages.filter(this.filterMessages).length
    ) {
      this.setState({
        newMessagesClass: 'animationDown',
        newMessagesContent: (
          <>
            <Button
              onClick={this._closeAndScrollToNewMessage}
              variant="primary"
              type="button"
              className="new-message-button"
            >
              <span className="button-content">
                <Icon name="arrow-down" className="arrow-down" />
                New Message
              </span>
            </Button>
            <IconButton
              type="button"
              icon="close"
              className="close-new-messages"
              onClick={() => {
                this.setState({
                  newMessagesClass: 'animationUp',
                  newMessagesContent: null,
                });
              }}
            />
          </>
        ),
      });
    }
  }

  /**
   * Checks if legacy components are used for rendering email message and
   * scrolling and applies a dirty fix to move the scroll to the desired
   * location.
   *
   * When using iframe for rendering emails, the height of the message changes a
   * few times while loading. This causes a lot of changes in scrolling
   * position, which will result the scroll not staying in the desired location.
   * To fix this, we have to move the scroll to desired position after some
   * time. This is fine because the conversation takes long to load and it's not
   * like we move scroll long after the page is ready.
   *
   * This will be removed once both feature-flags are removed.
   */
  moveScrollToLastItemIfNeeded(): void {
    if (
      !this.props.useShadowDom &&
      this.props.metaConversation.conversation.initial_channel === 'email'
    ) {
      setTimeout(() => {
        this.scrollToLastItem();
      }, 1500);
    }
  }

  setSummary(summary: null | ConversationSummaryInfoSummary = null): void {
    this.props.conversationSetSummary(summary);
  }

  filterMessages(msg: Message) {
    return msg.type === 'conversation:message_added';
  }

  isConversationPhoneCall(channel: string) {
    return PHONE_BASED_CHANNELS.includes(channel);
  }

  getAssistantMessages() {
    return this.props.messages.filter((message: Message) => {
      return [
        'conversation:conversational_assistant_menu',
        'conversation:conversational_assistant_message',
        'conversation:conversational_assistant_input',
      ].includes(message.type);
    });
  }

  getRenderMessages(): Array<Message> {
    const renderableMessages = this.props.messages.filter(
      (message: Message) => {
        return renderedMessageTypes.includes(
          message.type as RenderedMessageType,
        );
      },
    );

    return this.getRenderedCustomFormMessages(renderableMessages);
  }

  /**
   * Custom forms are rendered as two messages: one for the form and one for the
   * form submission. This function removes the form message if there's already
   * a submission message for the same form. This is to avoid showing the same
   * form twice.
   */
  getRenderedCustomFormMessages(messages: Message[]): Message[] {
    return messages.reduce(
      (acc: Array<Message>, message: Message, _, allMessages) => {
        const isCustomFormMessage =
          message.type === 'conversation:message_added' &&
          Boolean(message.form_id) &&
          Boolean(message.form_version);

        const isCustomFormWithResponseMessage =
          isCustomFormMessage && Boolean(message.form_submission_id);

        const isCustomFormWithoutResponseMessage =
          isCustomFormMessage && !isCustomFormWithResponseMessage;

        if (!isCustomFormWithoutResponseMessage) {
          return [...acc, message];
        }

        // Find the correcsponding form submission message
        const formSubmissionMessage = allMessages.find(
          ({ form_id, form_version, form_submission_id, type }: Message) =>
            type === 'conversation:message_added' &&
            form_id === message.form_id &&
            form_version === message.form_version &&
            Boolean(form_submission_id),
        );

        if (formSubmissionMessage) {
          // No need to include this form because a submission event for this
          // form already exist and that will serve as the form message
          return acc;
        }

        // Only include this form if there's no submission event for it
        return [...acc, message];
      },
      [],
    );
  }

  scrollToLastItem() {
    // Clear previous timeout to limit scroll occurrences
    if (this.scrollToLastItemTimeout !== null) {
      clearTimeout(this.scrollToLastItemTimeout);
      this.scrollToLastItemTimeout = null;
    }

    this.scrollToLastItemTimeout = window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        const scrollingOptions: ScrollIntoViewOptions = {
          // Use auto because smooth is too performance heavy
          behavior: 'auto',
          block: 'start',
          inline: 'nearest',
        };

        this.reverseScrollRef.current?.scrollToLastChild(scrollingOptions);
      });
    }, 0);
  }

  onScrollHandler(e: any) {
    // Decide we should auto scroll to the bottom or not.
    const tolerance = 128;
    const isScrollAtBottom = Math.abs(e.target.scrollTop) < tolerance;

    if (isScrollAtBottom) {
      this.setState({
        scrollAtBottom: true,
        newMessagesClass: 'animationUp',
        newMessagesContent: null,
      });
    } else if (this.props.asyncStatus.getConversation === 'succeeded') {
      this.setState({ scrollAtBottom: false });
    }
  }

  _closeAndScrollToNewMessage() {
    this.setState({
      newMessagesClass: 'animationUp',
      newMessagesContent: null,
    });

    this.reverseScrollRef.current?.scrollToEnd();
  }

  scrollToBottom = () => {
    if (this.state.scrollAtBottom === true) {
      this.reverseScrollRef.current?.scrollToEnd();
    }
  };

  getAuthorFromUser(user: User) {
    const initials = !user.avatar_url
      ? user?.name
          ?.split(' ')
          ?.map((name: string) => name[0])
          ?.join('')
      : null;
    return {
      image: user.avatar_url || null,
      name: user.name,
      initials: initials,
      id: user.id,
    };
  }

  onFetchPrinter = (url: string) => {
    this.props
      .originalEmailGetPrinterFriendly({ url: url })
      .then((response) => {
        // Currently prienter friendly markdown seems to be attached to response object.
        // So unless it is not defined use it, other than that
        // `contactform` conversation seem to return empty response for `originalEmailHtmlGetByType`
        const rewrittenHtml =
          response.msg?.rewritten_html || response.rewritten_html;
        (rewrittenHtml
          ? Promise.resolve(rewrittenHtml)
          : this.props.originalEmailHtmlGetByType({
              url: `${response.requestURL}`,
              type: 'rewritten_html',
            })
        )
          .then((htmlResponse) => {
            this.setState({ htmlIframeSrc: htmlResponse });
            const myIframe = document.getElementById('printer-friendly');
            if (!myIframe) {
              console.warn('Failed to init print window.');
              return;
            }

            this.timeouts.push(
              window.window.setTimeout(() => {
                (myIframe as any).contentWindow.print();
              }, 1000),
            );
          })
          .catch((exception) => {
            console.error('originalEmailHtmlGetByType exception', exception);
          });
      })
      .catch((exception) => {
        console.error('Exception', exception);
      });
  };

  renderParentLink() {
    const {
      metaConversation: { parentConversation, conversation },
    } = this.props;
    if (parentConversation && conversation.link_type === 'email_forward') {
      return (
        <StatusIsForward
          csid={parentConversation.id as any}
          className={this.getClassName('status')}
        />
      );
    }
    return null;
  }

  getErrorReasonMessage(rawType: string | null, rawDescription: string | null) {
    const type = (rawType ?? '').replace('-', '_');
    const description = (rawDescription ?? '').replace('-', '_');

    switch (description) {
      // Mandrill-specific failures
      case 'spam_filter':
        return 'The receiver has marked the sending email address as spam. A spam complaint occurs when the recipient clicks the “report spam” button in their email client. The email could not be delivered. Try again in 24 hours.';
      case 'invalid_domain':
        return 'The email domain that you tried to reach does not exist. Delivery is not possible. Will not retry.';

      // Common failures
      case 'bad_mailbox':
        return 'Bad mailbox: One or more of the email addresses you are trying to reach does not exist. The message was therefore not sent. The system will not try to resend the message to that/those email addresses. To solve the issue please check the email address you are trying to reach and try resending it in a new message to the corrected email address.';
      case 'suppressed':
        return 'Suppressed: One or more of the email addresses you are trying to reach is blacklisted by our email provider and was therefore not sent. Please reach out to Dixa Support to resolve the issue.';
      case 'attachment_rejected':
        return "Attachment Rejected: One or more of the recipients' mail servers has rejected the message due to an unacceptable attachment format or the attachment is too large. To resolve this issue, please try again after removing or modifying the attachment(s).";
      case 'content_rejected':
        return "Content Rejected: One or more of the recipients' email servers has rejected the message based on the content of the message. To resolve this issue, please try again after removing or modifying the content of the message.";
      case 'message_too_large':
        return "Message too large: One or more of the recipients' email servers did not accept the message because the message content (text) is too big or long. To resolve this issue, please try again after reducing the size of the message.";
      case 'mailbox_full':
        return "Mailbox full: One or more of the recipients' mail servers is not accept the message because their mailbox is full and the message could not be delivered. The system will not automatically retry on these types of errors. Usually a succesful attempt can be made after 48 hours.";
      case 'rejected':
        return 'Rejected: The sending mail server has flagged the content of the message as malicious and was not send. The system will not automatically retry on these types of errors. Please modify the content of the message and try again.';

      default:
        switch (type) {
          // Mandrill-specific failure types
          case 'reject':
            return 'Reject: The system has flagged the sending mail account of this message as blacklisted. The system will not automatically retry on these types of errors. Please reach out to Dixa Support to resolve the issue.';
          case 'spam':
            return "Spam: One or more of the recipients' mail servers did not accept the message because the sending email account has been marked as Spam. The system will not automatically retry on these types of errors. A spam complaint occurs when a recipient marks a message as spam in their email client. To resolve this please reach out on a different sending email.";

          // Common failure types
          case 'hard_bounce':
            return "Hard bounce: One or more of the recipients' email servers rejected the message. The system will not automatically retry on these types of errors. To solve the issue please verify the recipient's email address is correct and try to send the message again. If problems persist, please reach out to Dixa Support.";
          case 'soft_bounce':
            return 'Soft bounce: One or more of the email addresses you are trying to reach is not accepting the message at this point. Please try again later - we recommend to wait 48 hours.';
          case 'complaint':
            return 'Complaint: The email was delivered but it was flagged as spam. Please avoid sending further messages to this email address to avoid degradation of your email (spam) reputation.';
          default:
            return 'Unknown error: In trying to send the message (one of) the recipient(s) responded with an error message not known to the Dixa platform and rejected the message. The system will not automatically retry on unknown errors. We recommend to try again later - wait at least 48 hours before retrying. If sending should still fail, please contact Dixa Support for further assistance.';
        }
    }
  }

  getConversationID() {
    return this.props.metaConversation.conversation.id;
  }

  getConversationChannel() {
    const channel = this.props.metaConversation.conversation.initial_channel;
    return typeof channel === 'string' ? channel : '';
  }

  getConversation() {
    if (this.getConversationID()) {
      this.props
        .conversationGet({
          csid: this.getConversationID(),
        } as any)
        .catch((err) => {
          console.error('Caught', err);
          return err;
        });
    }
  }

  isLastMessageToIncludeInSummarization(message: Message): boolean {
    const {
      metaConversation: { messages },
    } = this.props;
    const textualMessages = messages.filter((message) =>
      includedInSummaryMessageTypes.includes(
        message.type as RenderedMessageType,
      ),
    );

    return (
      textualMessages.findIndex(
        (currentMessage) => currentMessage.id === message.id,
      ) ===
      textualMessages.length - 1
    );
  }

  renderMessage(
    message: Message,
    messageIndex: number,
    allMessages: Array<Message>,
  ): JSX.Element | null {
    const predictedQualityScoreData =
      derivePredictedQualityScoresForMessages(allMessages);
    const { enableCallRecordings, metaConversation } = this.props;
    const { conversation } = metaConversation;
    const conversationChannel = this.getConversationChannel();

    const requesterId = conversation.requested_by;

    const internal = [
      'conversation:wrapup_message',
      'conversation:wrapup_added',
    ].includes(message.type);
    const own = message.author.id === this.props.thisUserId;
    const isAgent = message.author.id !== requesterId;
    const first = messageIndex === 0;
    const newest = messageIndex === allMessages.length - 1;
    const {
      email_send_error = '',
      email_send_error_description = '',
      email_send_error_smtp_response = '',
    } = message;
    const conversationAttributes = conversation.attributes;
    const isInstagram =
      message.channel === 'facebookmessenger' &&
      !!conversation.attributes?.instagram_page_id &&
      !!conversation.attributes?.instagram_profile_id;

    const simple =
      isInstagram && message.is_story
        ? false
        : message.isSameSender && !internal;

    const predictedQualityScore = message.id
      ? predictedQualityScoreData[message.id]
      : undefined;

    const messageProps: MessageProps = {
      conversationAttributes,
      simple,
      className: classnames({
        [this.getClassName('message')]: true,
        [this.getClassName('message--continued')]: simple,
        [this.getClassName('message--nofill')]:
          !own &&
          !isAgent &&
          message.channel &&
          !['pstnphone', 'pstn_phone', 'callback', 'voicemail'].includes(
            message.channel,
          ) &&
          !internal,
      }),
      own,
      isAgent,
      newest,
      first,
      internal,
      catchInternalLinks: internal,
      // @ts-expect-error because this should be a string, not a number
      date: message.created_at || Date.now() / 1000,
      message: message,
      author: message.author,
      key: message.id ?? undefined,
      loading: !message.hasReceivedResponse,
      failed:
        message.failedResponse || email_send_error
          ? this.getErrorReasonMessage(
              email_send_error,
              email_send_error_description,
            )
          : false,
      failureReason: email_send_error_smtp_response ?? undefined,
      markup: undefined,
      isAutoReply: message.is_auto_reply,
      renderedTemplateName: message.rendered_template_name,
      conversation,
      isAnonymized: message.anonymized_at ? true : false,
      predictedQualityScore,
      sourceLinks: message.source_links,
      autoRedactionFailureReason:
        message.auto_redaction_failure_reason ?? undefined,
    };

    const statusProps = {
      message,
      key: message.id,
      className: this.getClassName('status'),
      date: message.created_at || Date.now() / 1000,
    };

    /* Messages */
    if (
      ['conversation:add_message', 'conversation:message_added'].includes(
        message.type,
      )
    ) {
      let Component: any | null = null;

      messageProps.computedDeliveryEvent =
        conversation.message_delivery_status && message.id
          ? conversation.message_delivery_status[message.id]
          : undefined;
      messageProps.messageId = message.id ?? undefined;
      messageProps.csid = conversation.id ?? undefined;

      // Handle email-specific rendering
      if (
        message.channel &&
        ['email', 'contactform'].includes(message.channel)
      ) {
        const originalUrl = message.email_original_content_url;
        if (originalUrl) {
          // For fetching text/markdown email.
          messageProps.originalEmailMeta = this.props.originalEmailMeta;

          // For showing original email.
          messageProps.onShowOriginal = () => {
            this.props.originalEmailGet({
              url: prepareOriginalEmailUrl(originalUrl),
            });
          };

          // For fetching HTML email.
          messageProps.originalUrl = prepareOriginalEmailUrl(originalUrl);
          messageProps.htmlMessages = this.props.htmlMessages;
          messageProps.originalEmailGetHtmlMessage =
            this.props.originalEmailGetHtmlMessage;

          // For showing printing email.
          messageProps.onFetchPrinter = () => {
            this.onFetchPrinter(prepareOriginalEmailUrl(originalUrl));
          };
          // Switch here between HTML and text email. / MessageEmail
          Component = MessageEmailHTML;
        } else {
          Component = MessageEmail;
        }
      } else if (
        message.channel &&
        [
          'widgetchat',
          'messenger',
          'adachat',
          'digitalgeniuschat',
          'kindlychat',
          'genericapimessaging',
          'trustpilotreviews',
        ].includes(message.channel)
      ) {
        Component = MessageWidgetChat;
      } else if (
        message.channel &&
        ['pstnphone', 'pstn_phone', 'callback', 'voicemail'].includes(
          message.channel,
        )
      ) {
        Component = message.is_transcript
          ? MessageTranscript
          : message.channel === 'voicemail'
            ? MessageVoicemail
            : MessagePhone;

        const recordingUrl = message.recording_url ?? message.voicemail_url;

        if (
          enableCallRecordings
            ? accessControl.hasAccess('conversation.call_recording') &&
              recordingUrl
            : false
        ) {
          messageProps.recordingURL = recordingUrl ?? undefined;
        }
      } else if (message.channel === 'whatsapp') {
        /* After migrating to the new WhatsApp integration and the ability for new messages to contain both images and text,
        it is necessary to remove "(No text)" for old legacy whatsApp messages.*/
        if (messageProps.message.text?.toLowerCase() === '(no text)') {
          messageProps.message.text = undefined;
        }

        Component = MessageWhatsApp;
      } else if (isInstagram) {
        Component = MessageInstagram;
      } else if (message.channel === 'facebookmessenger') {
        Component = MessageFacebook;
      } else if (message.channel === 'sms') {
        Component = MessageSms;
      } else if (message.channel === 'twitter') {
        Component = MessageTwitter;
      } else {
        Component = TextMessage;

        Sentry.captureMessage(
          `Unknown conversation channel detected: ${message.channel}`,
          {
            level: 'error',
            tags: {
              team: SentryTeam.Conversations,
            },
            extra: {
              channel: message.channel,
            },
          },
        );
      }

      return <Component {...messageProps} author={message.author} />;
    }
    if (internal) {
      // We need markup for these
      return <TextMessage {...messageProps} />;
    }
    if (
      'conversation:closed' === message.type &&
      this.isConversationPhoneCall(conversationChannel)
    ) {
      return <StatusClosed {...statusProps} own={messageProps.own ?? false} />;
    }
    if (
      'conversation:agent_unassigned' === message.type &&
      this.isConversationPhoneCall(conversationChannel)
    ) {
      return <StatusUnassigned {...statusProps} />;
    }
    if ('newMissedMessagesDevider' === message.type) {
      return <NewMessagesDevider {...statusProps} />; // Revisit
    }
    if (
      'conversation:transfer_initiated' === message.type &&
      this.isConversationPhoneCall(conversationChannel)
    ) {
      return <StatusTransferStarted {...statusProps} />;
    }
    if (
      'conversation:transfer_status' === message.type &&
      this.isConversationPhoneCall(conversationChannel)
    ) {
      return <StatusTransferStatus {...statusProps} />;
    }
    if (
      'conversation:transfer_answered' === message.type &&
      this.isConversationPhoneCall(conversationChannel)
    ) {
      return <StatusTransferAnswered {...statusProps} />;
    }
    if ('conversation:rated' === message.type) {
      // rating is displayed in the conversation sidebar
      return null;
    }
    if ('conversation:auto_reply_sent' === message.type) {
      return (
        <StatusAutoReply
          templateName={message.template_name}
          {...statusProps}
          date={statusProps.date.toString()}
        />
      );
    }

    return null;
  }

  renderMessagesAndSummary(): JSX.Element | null {
    const { metaConversation } = this.props;
    const { conversation } = metaConversation;
    const summary = conversation.summary?.current;
    const canGenerateSummary = !!conversation.summary?.can_be_generated;
    const messages = this.getRenderMessages();

    if (!conversation.id || !messages.length) {
      return null;
    }

    const lastMessage = findLast(messages, (message) => {
      return message.type === 'conversation:message_added';
    });

    return (
      <ReverseScroll
        /** @ts-expect-error Type inference not working correctly for this customized ref. */
        ref={this.reverseScrollRef}
        className={this.getClassName('messages-list')}
        onScroll={this.onScrollHandler}
      >
        {this.renderParentLink()}

        {messages.map((message, index, messages) => {
          const renderedMessage = this.renderMessage(message, index, messages);
          const isMessageUsedForSummaryGeneration =
            summary?.last_used_message_id === message.id;
          const isLastMessage = lastMessage?.id === message.id;

          if (
            canGenerateSummary &&
            (isMessageUsedForSummaryGeneration || (isLastMessage && !summary))
          ) {
            // If there is summary having this message id as last message, render
            // it right after the message so that it'll be sticky to the message.
            return (
              <Fragment key={index}>
                {renderedMessage}
                <ConversationSummaryByCsid
                  key={`csid-${conversation.id}-summary`}
                  className={this.getClassName('summary')}
                  view="details"
                  csid={conversation.id as number}
                  onSummaryLoaded={this.setSummary}
                  isStale={!this.isLastMessageToIncludeInSummarization(message)}
                  scrollIntoViewOnMount
                />
              </Fragment>
            );
          }

          return renderedMessage;
        })}
      </ReverseScroll>
    );
  }

  getClassName(className: string): string {
    return `messages__${className}`;
  }

  render() {
    let loading: JSX.Element | null = null;
    let animationClass = 'hidden';
    const { metaConversation, asyncStatus } = this.props;
    const { conversation } = metaConversation;
    const hasConversationID = !!this.getConversationID();
    /*
      Loading stuff: Can be moved to component
     */
    if (asyncStatus.getConversation === 'succeeded') {
      loading = null;
      animationClass = 'animationUp';
    } else if (
      asyncStatus.getConversation === 'loading' &&
      (!metaConversation || metaConversation.messages.length === 0)
    ) {
      loading = <div className="messages-notification">Loading...</div>;
      animationClass = 'animationDown';
    } else if (asyncStatus.getConversation === 'failed') {
      loading = (
        <div className="messages-notification">
          {hasConversationID ? (
            <Button variant="primary" onClick={this.getConversation}>
              Error loading conversation - try again
            </Button>
          ) : (
            <Button variant="primary">Error loading conversation</Button>
          )}
        </div>
      );
      animationClass = 'animationDown';
    }

    /** Typing indicator: Can be moved to component */
    const renderTyping = (typing: TypingType, i: number) => {
      if (conversation.id === typing.csid) {
        return (
          <Typing
            key={i}
            authors={conversation.authors as any}
            typing={typing as any}
            onShow={this.scrollToBottom}
          />
        );
      }
      return null;
    };

    return (
      <>
        <div
          key={conversation.id}
          id="messages"
          className="messages"
          onScroll={this.onScrollHandler}
          aria-live="polite"
          aria-relevant="additions"
          aria-atomic="false"
          role="log"
        >
          <MessageSectionCommandHandler
            reverseScrollRef={this.reverseScrollRef}
          />

          <AssistantSummary messages={this.getAssistantMessages()} />

          {this.renderMessagesAndSummary()}

          {conversation.is_abandoned && <StatusAbandoned />}

          {this.props.typings.map(renderTyping)}

          <OriginalEmailModal />

          <RedactMessageModal />

          <EmailHeadersModal />

          <PrinterFriendly htmlIframeSrc={this.state.htmlIframeSrc} />

          <div className={`text-center loading-content ${animationClass}`}>
            {loading}
          </div>

          <div
            className={`text-center new-messages-content ${this.state.newMessagesClass}`}
          >
            {this.state.newMessagesContent}
          </div>
        </div>
      </>
    );
  }
}

const MessageSectionCommandHandler = ({
  reverseScrollRef,
}: {
  reverseScrollRef: React.RefObject<ReverseScrollRef>;
}): null => {
  useCommandHandler({
    name: 'conversation:messagesScrollToTop',
    actionFn: () => {
      reverseScrollRef.current?.scrollToStart();
    },
  });

  useCommandHandler({
    name: 'conversation:messagesScrollToBottom',
    actionFn: () => {
      reverseScrollRef.current?.scrollToEnd();
    },
  });

  return null;
};
